import React from 'react';
import {Row, Col, Form} from 'react-bootstrap';
import * as yup from 'yup';
import {BaseSettingsForm} from '@/components/forms';
import axios, {AxiosResponse} from 'axios';
import {Story} from 'entities/story';
import {useStoreActions, useStoreState} from 'store';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import i18n from 'i18n';
import {buildStoryURL} from '@/utils/urlBuilder';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {navigate} from '@reach/router';

interface Props {
    storyId: number | null;

    onDone: () => void;
}

interface StoryPostData {
    title: string;
    public?: boolean;
}


const StorySettingsFormSchema: yup.ObjectSchema<StoryPostData> = yup.object({
    title: yup.string()
        .max(255, i18n.t('story_settings_title_max_length.text'))
        .required(i18n.t('story_settings_required_field.text')),
    public: yup.boolean(),
});

const StorySettingsForm = React.memo<Props>((
    {
        storyId,
        onDone,
    },
) => {
    const addOrEditStory = useStoreActions((actions) => actions.stories.addOrEditStory);
    const setReloadPublicStories = useStoreActions((actions) => actions.publicStories.setReload);
    const {userData} = useOptimizedConfig();
    const menuStructure = useStoreState(state => state.sidebarMenu.menuStructure);

    const userCanPublishStory = hasPermission('fs.story.make_public.execute', userData?.permissions, true);

    const initialValues: StoryPostData = {
        title: '',
        public: false,
    };

    const customSubmissionHandler = async (values: StoryPostData, formik: any) => {
        let requestMethod;
        let url = window.URLS.storiesResource;

        if (storyId) {
            url = buildStoryURL('storyDetails', storyId);
            requestMethod = axios.put;
        } else {
            requestMethod = axios.post;
        }

        const response = await requestMethod<Story, AxiosResponse<Story, StoryPostData>, StoryPostData>(url, values);

        // Get current story data for comparison
        const currentStory = formik.values;
        let reloadPublicStories = true;

        addOrEditStory(response.data);

        if (!userCanPublishStory) {
            reloadPublicStories = false;
        }

        if (!storyId && values.public === false) {
            reloadPublicStories = false;
        }

        if (currentStory && currentStory.public === values.public) {
            reloadPublicStories = false;
        }

        if (reloadPublicStories) {
            setReloadPublicStories(true);
        }

        // Only redirect to homepage if editing an existing story that the user doesn't own
        // and they're making it private (this seems to be for security/access control)
        if (storyId && currentStory && currentStory.createdBy?.id !== userData.id && values.public === false) {
            const {getRootRedirectUrl} = await import('../../utils/rootUrlResolver');
            navigate(getRootRedirectUrl({userData, menuStructure}));
            return response;
        }

        onDone();
        return response;
    };

    return (
        <BaseSettingsForm
            entityId={storyId || undefined}
            resourceUrl={window.URLS.storiesResource}
            storeActionPath="stories.addOrEditStory"
            validationSchema={StorySettingsFormSchema}
            initialValues={initialValues}
            successMessageKey="jt_story_saved.text"
            customSubmissionHandler={customSubmissionHandler}
        >
            {(formik, loading) => (
                <>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="storyTitle" >
                                <Form.Label>{i18n.t('jt_story_title.text')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="title"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.title}
                                    isInvalid={formik.touched.title && !!formik.errors.title}
                                    autoFocus
                                    data-cy="story-settings-title-input"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.title}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    {userCanPublishStory && (
                        <Row>
                            <Col xs={12}>
                                <Form.Group className="mb-3" >
                                    <Form.Check
                                        name="public"
                                        label={i18n.t('es_publish_for.text')}
                                        type="switch"
                                        onChange={formik.handleChange}
                                        checked={formik.values.public}
                                        isInvalid={!!formik.errors.public}
                                        feedback={formik.errors.public}
                                        id="storySettingsPublic"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    )
                    }

                    {/* Submit Button */}
                    < Row className="mt-4" >
                        <Col xs={12}>
                            <div className="d-flex justify-content-end">
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={formik.isSubmitting || !formik.isValid}
                                    data-cy="story-settings-save-btn"
                                >
                                    {formik.isSubmitting
                                        ? i18n.t('jt_saving.text')
                                        : (storyId ? i18n.t('jt_update.text') : i18n.t('jt_save.text'))
                                    }
                                </button>
                            </div>
                        </Col>
                    </Row >
                </>
            )}
        </BaseSettingsForm >
    );
});


export default StorySettingsForm;
